import { getCoverDisplayUrl, getCoverThumbUrl } from '../utils/coverUrl';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  X, MessageCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ReviewSection from '../components/ReviewSection';
import BookReader from '../components/BookReader';
import EpubReader from '../components/EpubReader';
import AudiobookPlayer from '../components/AudiobookPlayer';
import { ChatPanel } from './CommunityChat';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import MediaCarousels from '../components/book/MediaCarousels';
import MetaEditorModal from '../components/book/MetaEditorModal';
import PressReleaseSection from '../components/book/PressReleaseSection';
import PurchaseLinksEditor from '../components/book/PurchaseLinksEditor';
import SeriesContinuation from '../components/book/SeriesContinuation';
import TrailerSection from '../components/book/TrailerSection';
import AuthorBioSection from '../components/book/AuthorBioSection';
import HeroActions from '../components/book/HeroActions';
import TrailerUploadModal from '../components/book/TrailerUploadModal';
import AdminControlBar from '../components/book/AdminControlBar';
import RecommendationsRow from '../components/book/RecommendationsRow';
import { EmbedCodeDialog } from '../components/EmbedCodeDialog';
import HeroHeader from '../components/book/HeroHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const getCoverUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${window.location.origin}${url}`;
  return url;
};

const BookDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReader, setShowReader] = useState(false);
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [isPlayerMinimized, setIsPlayerMinimized] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const userRole = localStorage.getItem('userRole');
  const token = localStorage.getItem('token');
  const { toast } = useToast();

  // Trailer upload modal toggle (logic moved into TrailerUploadModal)
  const [showTrailerUpload, setShowTrailerUpload] = useState(false);

  // Purchase links state (Admin only)
  const [showLinksEditor, setShowLinksEditor] = useState(false);
  const [amazonLink, setAmazonLink] = useState('');
  const [barnesNobleLink, setBarnesNobleLink] = useState('');
  const [authorWebsiteLink, setAuthorWebsiteLink] = useState('');
  const [savingLinks, setSavingLinks] = useState(false);

  // Book meta state (Admin only - author bio, series, media embeds)
  const [showMetaEditor, setShowMetaEditor] = useState(false);
  const [editAuthorBio, setEditAuthorBio] = useState('');
  const [editSeriesName, setEditSeriesName] = useState('');
  const [editSeriesOrder, setEditSeriesOrder] = useState('');
  const [editYoutubeUrl, setEditYoutubeUrl] = useState('');
  const [editYoutubeUrl2, setEditYoutubeUrl2] = useState('');
  const [editSoundcloudUrl, setEditSoundcloudUrl] = useState('');
  const [editSpotifyUrl, setEditSpotifyUrl] = useState('');
  const [editMediaLinks, setEditMediaLinks] = useState([]);
  const [editTrailerExternalUrl, setEditTrailerExternalUrl] = useState('');
  const [editMediaOrder, setEditMediaOrder] = useState([]);
  const [editMediaPinned, setEditMediaPinned] = useState([]);
  const [editDisabledEmbeds, setEditDisabledEmbeds] = useState([]);
  const [editGenres, setEditGenres] = useState([]);
  const [editTitle, setEditTitle] = useState('');
  const [editAuthor, setEditAuthor] = useState('');
  const [renameAllBooks, setRenameAllBooks] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [editDescription, setEditDescription] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [reuploadingFile, setReuploadingFile] = useState(false);
  const [pressReleaseTick, setPressReleaseTick] = useState(0);

  // My List / Like / Share state
  const [inMyList, setInMyList] = useState(false);
  const [liked, setLiked] = useState(false);

  // Series and Recommendations state
  const [seriesBooks, setSeriesBooks] = useState(null);
  const [similarBooks, setSimilarBooks] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const handleAddToList = async () => {
    if (!token) {
      toast({ title: 'Please log in', description: 'Sign in to add books to your list', variant: 'destructive' });
      return;
    }
    try {
      if (inMyList) {
        const res = await axios.get(`${API}/library/bookmarks`, { headers });
        const bm = res.data.find((b) => b.book_id === id);
        if (bm) await axios.delete(`${API}/library/bookmarks/${bm.id}`, { headers });
        setInMyList(false);
        toast({ title: 'Removed from My List' });
      } else {
        await axios.post(`${API}/library/bookmarks`, { book_id: id, label: 'My List' }, { headers });
        setInMyList(true);
        toast({ title: 'Added to My List' });
      }
    } catch {
      toast({ title: 'Error', description: 'Could not update your list', variant: 'destructive' });
    }
  };

  const handleLike = async () => {
    if (!token) {
      toast({ title: 'Please log in', description: 'Sign in to like books', variant: 'destructive' });
      return;
    }
    if (!liked) {
      axios.post(`${API}/like-view?book_id=${id}`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
    setLiked(!liked);
    toast({ title: liked ? 'Like removed' : 'You liked this book!' });
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: book?.title, text: `Check out "${book?.title}" on Seven Chapter Books!`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast({ title: 'Link copied!', description: 'Book link copied to clipboard' });
      }
    } catch {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied!', description: 'Book link copied to clipboard' });
    }
  };

  // Check if book is already in user's list
  useEffect(() => {
    if (token && id) {
      axios
        .get(`${API}/library/bookmarks`, { headers })
        .then((res) => {
          const found = res.data?.find((b) => b.book_id === id);
          if (found) setInMyList(true);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  const fetchBook = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/books/${id}`);
      const bookData = response.data;
      if (bookData.book_file_url && !bookData.book_file_type) {
        try {
          const fileId = bookData.book_file_url.replace('/api/db-files/', '');
          const headResp = await axios.get(`${API}/file-info/${fileId}`);
          if (headResp.data?.content_type) bookData.book_file_type = headResp.data.content_type;
        } catch {
          bookData.book_file_type = 'application/pdf';
        }
      }
      setBook(bookData);
      setAmazonLink(bookData.amazon_link || '');
      setBarnesNobleLink(bookData.barnes_noble_link || '');
      setAuthorWebsiteLink(bookData.author_website_link || '');
      setEditAuthorBio(bookData.author_bio || '');
      setEditSeriesName(bookData.series_name || '');
      setEditSeriesOrder(bookData.series_order || '');
      setEditYoutubeUrl(bookData.youtube_url || '');
      setEditYoutubeUrl2(bookData.youtube_url_2 || '');
      setEditSoundcloudUrl(bookData.soundcloud_url || '');
      setEditSpotifyUrl(bookData.spotify_url || '');
      setEditMediaLinks(bookData.media_links || []);
      setEditTrailerExternalUrl(bookData.trailer_external_url || '');
      setEditMediaOrder(bookData.media_order || []);
      setEditMediaPinned(bookData.media_pinned || []);
      setEditDisabledEmbeds(bookData.disabled_embeds || []);
      setEditTitle(bookData.title || '');
      setEditAuthor(bookData.author || '');
      setRenameAllBooks(false);
      setEditDescription(bookData.description || '');
      setEditPrice(bookData.price !== undefined && bookData.price !== null ? String(bookData.price) : '');
      setEditGenres(Array.isArray(bookData.genres) ? bookData.genres : []);
    } catch (error) {
      console.error('Failed to fetch book:', error);
      toast({ title: 'Error', description: 'Failed to load book details.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchBook();
    if (token && id) {
      axios.post(`${API}/consume-view-credit?book_id=${id}`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      axios.post(`${API}/user/currently-reading`, { book_id: id, is_reading: true }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
    axios.get(`${API}/books/${id}/series`).then((res) => setSeriesBooks(res.data)).catch(() => {});
    axios.get(`${API}/books/${id}/similar`).then((res) => setSimilarBooks(res.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSaveLinks = async () => {
    setSavingLinks(true);
    try {
      await axios.put(
        `${API}/books/${id}/links`,
        {
          amazon_link: amazonLink || null,
          barnes_noble_link: barnesNobleLink || null,
          author_website_link: authorWebsiteLink || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Links saved!' });
      setShowLinksEditor(false);
      fetchBook();
    } catch {
      toast({ title: 'Error saving links', variant: 'destructive' });
    } finally {
      setSavingLinks(false);
    }
  };

  const handleSaveMeta = async () => {
    // Only send fields that actually changed compared to the loaded book.
    // This way editing "genres" can never accidentally wipe title/description
    // even if the modal's local state briefly diverges from the server's
    // canonical record.
    const payload = {};
    const eq = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

    const trimOrNull = (v) => {
      if (v === undefined || v === null) return null;
      const s = String(v).trim();
      return s === '' ? null : s;
    };

    if (editTitle.trim() && editTitle.trim() !== (book.title || '')) {
      payload.title = editTitle.trim();
    }
    if (editAuthor.trim() && editAuthor.trim() !== (book.author || '')) {
      payload.author = editAuthor.trim();
      if (renameAllBooks) payload.author_rename_all = true;
    }
    if (!eq(trimOrNull(editDescription), trimOrNull(book.description))) {
      payload.description = trimOrNull(editDescription);
    }
    const priceNum = editPrice === '' ? null : parseFloat(editPrice);
    if (priceNum !== null && !Number.isNaN(priceNum) && priceNum !== (book.price ?? null)) {
      payload.price = priceNum;
    }
    if (!eq(editGenres || [], book.genres || [])) payload.genres = editGenres || [];

    if (!eq(trimOrNull(editAuthorBio), trimOrNull(book.author_bio))) {
      payload.author_bio = trimOrNull(editAuthorBio);
    }
    if (!eq(trimOrNull(editSeriesName), trimOrNull(book.series_name))) {
      payload.series_name = trimOrNull(editSeriesName);
    }
    const seriesOrderNum = editSeriesOrder ? parseInt(editSeriesOrder, 10) : null;
    if (seriesOrderNum !== (book.series_order ?? null)) {
      payload.series_order = seriesOrderNum;
    }
    if (!eq(trimOrNull(editYoutubeUrl), trimOrNull(book.youtube_url))) {
      payload.youtube_url = trimOrNull(editYoutubeUrl);
    }
    if (!eq(trimOrNull(editYoutubeUrl2), trimOrNull(book.youtube_url_2))) {
      payload.youtube_url_2 = trimOrNull(editYoutubeUrl2);
    }
    if (!eq(trimOrNull(editSoundcloudUrl), trimOrNull(book.soundcloud_url))) {
      payload.soundcloud_url = trimOrNull(editSoundcloudUrl);
    }
    if (!eq(trimOrNull(editSpotifyUrl), trimOrNull(book.spotify_url))) {
      payload.spotify_url = trimOrNull(editSpotifyUrl);
    }
    if (!eq(editMediaLinks || [], book.media_links || [])) payload.media_links = editMediaLinks;
    if (!eq(trimOrNull(editTrailerExternalUrl), trimOrNull(book.trailer_external_url))) {
      payload.trailer_external_url = trimOrNull(editTrailerExternalUrl);
    }
    if (!eq(editMediaOrder || [], book.media_order || [])) payload.media_order = editMediaOrder;
    if (!eq(editMediaPinned || [], book.media_pinned || [])) payload.media_pinned = editMediaPinned;
    if (!eq(editDisabledEmbeds || [], book.disabled_embeds || [])) {
      payload.disabled_embeds = editDisabledEmbeds;
    }

    if (Object.keys(payload).length === 0) {
      toast({ title: 'No changes to save' });
      setShowMetaEditor(false);
      return;
    }

    setSavingMeta(true);
    try {
      const res = await axios.put(`${API}/books/${id}/meta`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const renamed = res.data?.renamed_other_books || 0;
      toast({
        title: 'Book info updated!',
        description: `Saved ${Object.keys(payload).length} field${Object.keys(payload).length > 1 ? 's' : ''}.${renamed > 0 ? ` Author renamed on ${renamed} other book${renamed > 1 ? 's' : ''} too.` : ''}`,
      });
      setShowMetaEditor(false);
      fetchBook();
      axios.get(`${API}/books/${id}/series`).then((res) => setSeriesBooks(res.data)).catch(() => {});
    } catch (e) {
      console.error('saveMeta error', e);
      toast({ title: 'Error updating book info', variant: 'destructive' });
    } finally {
      setSavingMeta(false);
    }
  };

  const handleAuthorPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Please select an image file', variant: 'destructive' });
      return;
    }
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('author_photo', file);
      await axios.post(`${API}/books/${id}/author-photo`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      toast({ title: 'Author photo uploaded!' });
      fetchBook();
    } catch {
      toast({ title: 'Error uploading photo', variant: 'destructive' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeleteBook = async () => {
    if (!window.confirm('Are you sure you want to delete this book? This action cannot be undone.')) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/books/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Book Deleted', description: 'The book has been successfully removed.' });
      window.location.href = '/';
    } catch (error) {
      console.error('Failed to delete book:', error);
      toast({
        title: 'Delete Failed',
        description: error.response?.data?.detail || 'Failed to delete book.',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleReuploadBookFile = async (file) => {
    if (!file) return;
    const lower = (file.name || '').toLowerCase();
    if (!(lower.endsWith('.pdf') || lower.endsWith('.epub'))) {
      toast({ title: 'Unsupported file', description: 'Only .pdf or .epub allowed', variant: 'destructive' });
      return;
    }
    setReuploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('book_file', file);
      const resp = await axios.post(`${API}/books/${id}/replace-file`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 5 * 60 * 1000, // big EPUBs can take a while
      });
      toast({
        title: 'Book file replaced',
        description: `New file is ${(resp.data?.size / (1024 * 1024)).toFixed(2)} MB`,
      });
      fetchBook();
    } catch (err) {
      toast({
        title: 'Re-upload failed',
        description: err.response?.data?.detail || 'Could not replace book file',
        variant: 'destructive'
      });
    } finally {
      setReuploadingFile(false);
    }
  };

  // Trailer upload is handled by TrailerUploadModal component
  const handleDeleteTrailer = async () => {
    if (!window.confirm('Are you sure you want to remove this trailer?')) return;
    try {
      await axios.delete(`${API}/books/${id}/trailer`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Trailer Removed', description: 'Book trailer has been removed.' });
      fetchBook();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to remove trailer.',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-xl mb-4">Book not found</p>
          <Button onClick={() => navigate('/')} className="bg-red-600 hover:bg-red-700">Go Back Home</Button>
        </div>
      </div>
    );
  }

  const handleReadBook = async () => {
    if (!book.book_file_url) {
      toast({ title: 'No Book File', description: 'This book does not have a readable file uploaded yet.', variant: 'destructive' });
      return;
    }
    // Pre-flight: confirm the file is actually present and complete on the
    // backend BEFORE mounting the reader iframe — otherwise the user sees
    // raw JSON ("Error loading file") in their browser's PDF viewer.
    try {
      const fileId = book.book_file_url.replace('/api/db-files/', '').split('?')[0];
      const info = await axios.get(`${API}/file-info/${fileId}`);
      if (!info.data?.exists) {
        toast({
          title: 'Book file unavailable',
          description: 'The uploaded file is missing on the server. Please re-upload the book.',
          variant: 'destructive'
        });
        return;
      }
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      toast({
        title: 'Could not open book',
        description: detail || 'The book file appears to be incomplete. Please re-upload the book.',
        variant: 'destructive'
      });
      return;
    }
    setShowReader(true);
  };
  const handleListenAudiobook = () => {
    if (book.audiobook_file_url || book.audiobook_chapters?.length > 0) {
      setShowAudioPlayer(true);
      setIsPlayerMinimized(false);
    } else {
      toast({ title: 'No Audio', description: 'This book does not have an audiobook available.', variant: 'destructive' });
    }
  };

  const trailerUrl = book?.trailer_url ? getCoverUrl(book.trailer_url) : null;
  const trailerExternalUrl = book?.trailer_external_url || null;
  const hasTrailer = trailerUrl || trailerExternalUrl;

  return (
    <div className="min-h-screen bg-black relative">
      {/* Fixed atmospheric cover backdrop — extends the book cover image through
          every section below the hero. Sits behind everything; the hero's own
          full-cover image overlays it during the first viewport, then this layer
          becomes visible as the user scrolls past the hero. Heavy blur + GPU
          compositing eliminate JPEG compression banding so dark areas read as
          smooth gradients rather than blocky lines. */}
      <div className="fixed inset-0 -z-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <img
          src={getCoverThumbUrl(book.cover_image_url || book.coverImage)}
          alt=""
          className="absolute inset-0 w-full h-full object-cover animate-kenburns"
          style={{
            filter: 'blur(80px) saturate(1.3)',
            willChange: 'transform',
            opacity: 0.85,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/55 to-black/80" />
      </div>

      {/* Hero Section */}
      <div className="relative min-h-screen z-10">
        <div className="absolute inset-0">
          <img src={getCoverDisplayUrl(book.cover_image_url || book.coverImage)} alt={book.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50" />
        </div>

        {/* Back Button & Admin Controls */}
        <div className="absolute top-20 left-8 z-20 flex items-center space-x-4">
          <button onClick={() => navigate(-1)} className="text-white hover:text-gray-300 transition-colors" data-testid="back-button">
            <X className="h-8 w-8" />
          </button>
          {userRole === 'admin' && (
            <AdminControlBar
              book={book}
              deleting={deleting}
              onDeleteBook={handleDeleteBook}
              onShowTrailerUpload={() => setShowTrailerUpload(true)}
              onDeleteTrailer={handleDeleteTrailer}
              showLinksEditor={showLinksEditor}
              onToggleLinksEditor={() => setShowLinksEditor(!showLinksEditor)}
              showMetaEditor={showMetaEditor}
              onToggleMetaEditor={() => setShowMetaEditor(!showMetaEditor)}
              reuploadingFile={reuploadingFile}
              onReuploadBookFile={handleReuploadBookFile}
            />
          )}
        </div>

        {/* Content (extracted) */}
        <HeroHeader book={book}>
          <HeroActions
            book={book}
            hasTrailer={hasTrailer}
            inMyList={inMyList}
            liked={liked}
            onReadBook={handleReadBook}
            onListenAudiobook={handleListenAudiobook}
            onAddToList={handleAddToList}
            onLike={handleLike}
            onShare={handleShare}
            onEmbed={() => setEmbedOpen(true)}
          />
        </HeroHeader>
      </div>

      {/* All sections below the hero share a relative z-10 stacking context so
          the fixed cover backdrop shows through their semi-transparent panels. */}
      <div className="relative z-10">
      {/* Book Trailer Section (extracted) */}
      <TrailerSection book={book} trailerUrl={trailerUrl} trailerExternalUrl={trailerExternalUrl} />

      {/* Author's Bio Section (extracted) */}
      <AuthorBioSection
        book={book}
        userRole={userRole}
        uploadingPhoto={uploadingPhoto}
        onAuthorPhotoUpload={handleAuthorPhotoUpload}
      />

      {/* Media Embeds (extracted) */}
      <MediaCarousels book={book} />

      {/* Press Release articles (FEAT-008) — hidden when none exist */}
      <PressReleaseSection bookId={id} refreshKey={pressReleaseTick} />

      {/* Reviews Section */}
      <div className="bg-black/55 backdrop-blur-sm py-12">
        <ReviewSection bookId={id} onReviewSubmitted={fetchBook} />
      </div>

      {/* Series Continuation (extracted) */}
      <SeriesContinuation seriesBooks={seriesBooks} currentBookId={id} navigate={navigate} />

      {/* AI Recommendations (extracted) */}
      <RecommendationsRow similarBooks={similarBooks} currentTitle={book?.title} navigate={navigate} />

      {/* Book Discussion Chat */}
      {token && (
        <div className="bg-black/55 backdrop-blur-sm py-12" data-testid="book-discussion-section">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <MessageCircle className="h-6 w-6 text-red-500" />
              Book Discussion
            </h2>
            <p className="text-gray-400 text-sm mb-4">Chat about "{book?.title}" with other readers</p>
            <div className="bg-gray-950 border border-white/10 rounded-xl overflow-hidden relative" style={{ height: '400px' }}>
              <ChatPanel room={id} roomName={book?.title || 'Book Discussion'} />
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Purchase Links Editor (extracted) */}
      <PurchaseLinksEditor
        open={showLinksEditor && userRole === 'admin'}
        onClose={() => setShowLinksEditor(false)}
        amazonLink={amazonLink}
        setAmazonLink={setAmazonLink}
        barnesNobleLink={barnesNobleLink}
        setBarnesNobleLink={setBarnesNobleLink}
        authorWebsiteLink={authorWebsiteLink}
        setAuthorWebsiteLink={setAuthorWebsiteLink}
        onSave={handleSaveLinks}
        saving={savingLinks}
      />

      {/* Meta Editor (extracted) */}
      <MetaEditorModal
        open={showMetaEditor}
        onClose={() => setShowMetaEditor(false)}
        book={book}
        userRole={userRole}
        uploadingPhoto={uploadingPhoto}
        onAuthorPhotoUpload={handleAuthorPhotoUpload}
        editAuthorBio={editAuthorBio} setEditAuthorBio={setEditAuthorBio}
        editSeriesName={editSeriesName} setEditSeriesName={setEditSeriesName}
        editSeriesOrder={editSeriesOrder} setEditSeriesOrder={setEditSeriesOrder}
        editYoutubeUrl={editYoutubeUrl} setEditYoutubeUrl={setEditYoutubeUrl}
        editYoutubeUrl2={editYoutubeUrl2} setEditYoutubeUrl2={setEditYoutubeUrl2}
        editSoundcloudUrl={editSoundcloudUrl} setEditSoundcloudUrl={setEditSoundcloudUrl}
        editSpotifyUrl={editSpotifyUrl} setEditSpotifyUrl={setEditSpotifyUrl}
        editTrailerExternalUrl={editTrailerExternalUrl} setEditTrailerExternalUrl={setEditTrailerExternalUrl}
        editMediaLinks={editMediaLinks} setEditMediaLinks={setEditMediaLinks}
        editMediaOrder={editMediaOrder} setEditMediaOrder={setEditMediaOrder}
        editMediaPinned={editMediaPinned} setEditMediaPinned={setEditMediaPinned}
        editDisabledEmbeds={editDisabledEmbeds} setEditDisabledEmbeds={setEditDisabledEmbeds}
        editGenres={editGenres} setEditGenres={setEditGenres}
        editTitle={editTitle} setEditTitle={setEditTitle}
        editAuthor={editAuthor} setEditAuthor={setEditAuthor}
        renameAllBooks={renameAllBooks} setRenameAllBooks={setRenameAllBooks}
        editDescription={editDescription} setEditDescription={setEditDescription}
        editPrice={editPrice} setEditPrice={setEditPrice}
        bookId={id}
        token={token}
        saving={savingMeta}
        onSave={handleSaveMeta}
        onCoverUpdated={fetchBook}
      />

      {/* Trailer Upload Modal (extracted) */}
      <TrailerUploadModal
        open={showTrailerUpload && userRole === 'admin'}
        onClose={() => setShowTrailerUpload(false)}
        bookId={id}
        token={token}
        onUploaded={fetchBook}
      />

      {/* Embed Book Card */}
      <EmbedCodeDialog
        open={embedOpen}
        onOpenChange={setEmbedOpen}
        embedUrl={`${API}/books/${id}/embed`}
        embedCode={`<iframe src="${API}/books/${id}/embed" width="100%" height="240" style="border:0;border-radius:12px;max-width:640px" title="${book?.title || 'Book'} — SevenChapterBooks" loading="lazy"></iframe>`}
        previewTitle={`${book?.title || 'Book'} — embed preview`}
        previewHeight={240}
      />

      {/* Book Reader */}
      {showReader && book.book_file_url && (
        (book.book_file_type === 'application/epub+zip' || book.book_file_url?.endsWith('.epub')) ? (
          <EpubReader bookUrl={book.book_file_url} bookId={book.id} totalPages={book.total_pages || 100} onClose={() => setShowReader(false)} />
        ) : (
          <BookReader bookUrl={book.book_file_url} audiobookUrl={book.audiobook_file_url} bookId={book.id} totalPages={book.total_pages || 100} onClose={() => setShowReader(false)} />
        )
      )}

      {/* Audiobook Player */}
      {showAudioPlayer && (book.audiobook_file_url || book.audiobook_chapters?.length > 0) && (
        <AudiobookPlayer
          audiobook={book}
          chapters={book.audiobook_chapters || []}
          isMinimized={isPlayerMinimized}
          onToggleMinimize={() => setIsPlayerMinimized(!isPlayerMinimized)}
          onClose={() => { setShowAudioPlayer(false); setIsPlayerMinimized(false); }}
        />
      )}
    </div>
  );
};

export default BookDetails;
